import type { RouteRecordRaw, RouterScrollBehavior } from 'vue-router'
import { appPathUrl } from '@/config'

export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/HomeView.vue'),
  },
  {
    path: '/how-it-works',
    name: 'HowItWorks',
    component: () => import('@/views/HowItWorksView.vue'),
  },
  {
    path: '/song',
    name: 'Song',
    component: () => import('@/views/SongView.vue'),
  },
  {
    path: '/pricing',
    name: 'Pricing',
    component: () => import('@/views/PricingView.vue'),
  },
  {
    path: '/referrals',
    name: 'Referrals',
    component: () => import('@/views/ReferralsView.vue'),
  },
  {
    path: '/download',
    name: 'Download',
    component: () => import('@/views/DownloadView.vue'),
  },
  {
    path: '/legal',
    name: 'Legal',
    component: () => import('@/views/LegalView.vue'),
  },
  {
    path: '/use-cases',
    name: 'UseCasesIndex',
    component: () => import('@/views/UseCasesIndexView.vue'),
  },
  {
    path: '/use-cases/:slug',
    name: 'UseCase',
    component: () => import('@/views/UseCaseView.vue'),
  },
  {
    path: '/models/:slug',
    name: 'Model',
    component: () => import('@/views/ModelView.vue'),
  },
  {
    path: '/whats-new',
    name: 'WhatsNew',
    component: () => import('@/views/WhatsNewView.vue'),
  },
  {
    path: '/checkout/success',
    name: 'CheckoutSuccess',
    component: () => import('@/views/CheckoutSuccessView.vue'),
    meta: { noindex: true },
  },
  {
    path: '/checkout/cancel',
    name: 'CheckoutCancel',
    component: () => import('@/views/CheckoutCancelView.vue'),
    meta: { noindex: true },
  },
  {
    // Desktop-membership return (checkout AND billing portal). Separate from
    // /checkout/success, which is token-pack-specific — see the view's header.
    path: '/checkout/membership',
    name: 'CheckoutMembership',
    component: () => import('@/views/CheckoutMembershipView.vue'),
    meta: { noindex: true },
  },
  {
    path: '/admin',
    name: 'AdminLogin',
    component: () => import('@/views/AdminLoginView.vue'),
    meta: { noindex: true },
  },
  {
    path: '/admin/downloads',
    name: 'AdminDownloads',
    component: () => import('@/views/AdminDownloadsView.vue'),
    meta: { noindex: true, requiresAdmin: true },
  },
  {
    path: '/ref/:code',
    name: 'Referral',
    component: () => import('@/views/NotFoundView.vue'),
    beforeEnter: (to) => {
      const code = to.params.code as string
      window.location.href = appPathUrl(`/login?mode=signup&ref=${encodeURIComponent(code)}`)
      return false
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { noindex: true },
  },
]

// Honour ?#hash links (e.g. "/#editor" from the homepage path-picker) and
// restore back/forward positions. Default is top-of-page. The setTimeout
// gives the lazy-loaded home view a tick to mount before we measure the
// target — without it the scroll fires against a section that hasn't
// rendered yet and lands at top:0.
export const scrollBehavior: RouterScrollBehavior = (to, _from, savedPosition) => {
  if (savedPosition) return savedPosition
  if (to.hash) {
    return new Promise((resolve) => {
      setTimeout(() => {
        const target = document.querySelector<HTMLElement>(to.hash)
        const declaredOffset = target ? Number.parseFloat(getComputedStyle(target).scrollMarginTop) : Number.NaN
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        resolve({
          el: to.hash,
          behavior: to.path === '/use-cases' ? 'instant' : reduceMotion ? 'auto' : 'smooth',
          top: Number.isFinite(declaredOffset) && declaredOffset > 0 ? declaredOffset : 72,
        })
      }, 250)
    })
  }
  return { top: 0 }
}
